import {
  Box,
  Card,
  Chip,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import type { SalesHarvestStrategyCampaign } from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";

import { salesApi } from "../client.js";
import { SalesErrorState } from "../components/SalesEmptyState.js";

/**
 * The CONTACT HARVESTER's strategy — shown on the Strategy tab when the agent is
 * in contacts mode (instead of the lead strategist's memo). It's a deterministic
 * plan, not an LLM memo: the mission, the enumerate→dig→verify→dedup→promote
 * method, the guardrails, and each campaign's target industries × regions with
 * live coverage.
 */
export function HarvesterStrategy(): JSX.Element {
  const strat = useQuery({
    queryKey: ["sales", "harvest", "strategy"],
    queryFn: salesApi.getHarvestStrategy,
    refetchInterval: 60_000,
  });

  if (strat.isLoading && !strat.data) return <PageLoader />;
  if (strat.isError) return <SalesErrorState error={strat.error} />;
  const d = strat.data;
  if (!d) return <PageLoader />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Contact-harvester strategy</Typography>
          <Chip label="Contacts mode" size="small" color="success" sx={{ borderRadius: 1 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This is what runs when the agent is in <strong>Contacts</strong> mode — different from the
          lead-finder's monthly memo.
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Mission</Typography>
        <Typography variant="body2">{d.mission}</Typography>
      </Card>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap>
        <Card variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Method</Typography>
          <Stack component="ol" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {d.method.map((m) => (
              <Typography key={m} component="li" variant="body2">{m}</Typography>
            ))}
          </Stack>
        </Card>
        <Card variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Rules</Typography>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {d.rules.map((r) => (
              <Typography key={r} component="li" variant="body2">{r}</Typography>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
            Email verification: {d.verifier_enabled ? "ON (paid verifier)" : "off — emails stay 'unverified guess' until a verifier key is set"}
            {" · "}re-digs every {d.re_dig_days} days
          </Typography>
        </Card>
      </Stack>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Campaigns — target industries × regions
        </Typography>
        <Stack spacing={2}>
          {d.campaigns.map((c) => <CampaignCard key={c.slug} campaign={c} />)}
        </Stack>
      </Box>
    </Box>
  );
}

function CampaignCard({ campaign: c }: { campaign: SalesHarvestStrategyCampaign }): JSX.Element {
  const pct = c.matrix_cells > 0 ? Math.round((c.cells_done / c.matrix_cells) * 100) : 0;
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 600 }}>{c.label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {c.cells_done}/{c.matrix_cells} cells swept · {c.cells_pending} queued
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ my: 1, borderRadius: 1, height: 6 }} />

      <Typography variant="caption" color="text.secondary">
        {c.verticals.length} industries × {c.regions.length} regions
      </Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {c.verticals.map((v) => (
          <Box key={v.vertical}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={v.label} size="small" sx={{ bgcolor: "surfaceSoft", borderRadius: 1, fontWeight: 500 }} />
              {v.registries.map((r) => (
                <Link key={r} href={r} target="_blank" rel="noreferrer" underline="hover" variant="caption">
                  registry ↗
                </Link>
              ))}
            </Stack>
            {v.sample_queries.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                e.g. “{v.sample_queries[0]}”
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
        Regions: {c.regions.join(", ")}
      </Typography>
    </Card>
  );
}
